import React, { Fragment, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import { space as styledSpace } from 'styled-system'
import { SwipeableDrawer } from '@material-ui/core'
import { Scrollbars } from 'react-custom-scrollbars-2'
import ClipLoader from 'react-spinners/ClipLoader'
import Alert from 'react-s-alert'
import { radius } from 'theme'
import { ERROR_MESSAGE } from 'consts'
import request from 'utils/request'
import errorHelper from 'utils/errorHelper'
import { getSocialNetworkIconByName } from 'utils/socialNetworks'
import { Flex, Grid } from 'components/atoms/Layout'
import { Text } from 'components/atoms/Typography'
import Divider from 'components/atoms/Divider'
import Image from 'components/atoms/Image'
import ImageWithFallback from 'components/atoms/ImageWithFallback'
import Poll from 'components/atoms/Poll'
import { ROUTE_CALENDAR } from '../../consts'

const StyledPreviewWrapper = styled(Flex)`
  position: relative;
  width: ${({ $isSmallPreview }) => ($isSmallPreview ? '280px' : `560px`)};
  padding: 0;
  border-radius: ${radius.l};
  ${styledSpace};
  display: flex;
  flex-direction: column;
  height: auto;
  border: 1px solid ${({ theme }) => theme.colors.border_color};
`

const StyledPicture = styled(ImageWithFallback)`
  border-radius: ${radius.pill};
`

const NetworkImageWrapper = styled(Flex)`
  position: absolute;
  bottom: 1px;
  right: 1px;
  justify-content: center;
  align-items: center;
  background-color: ${({ theme }) => theme.colors.white};
  border-radius: ${radius.m};
`

const iOS = process.browser && /iPad|iPhone|iPod/.test(navigator.userAgent)

const DEFAULT_SOURCE = '/assets/company.svg'

const SliderPollResultsPreview = ({ isOpen, data, setSliderPollResultsPreviewData, isSmallPreview }) => {
  const [isGettingResults, setIsGettingResults] = useState(true)
  const [pollResults, setPollResults] = useState({})

  const getResults = async () => {
    // specialEvents, type and type_id are not used. For now we would show only poll results published via Vista Social 04.09.2025
    const { eventIds = [], media_gid, specialEvents = [], type = '', type_id } = data
    try {
      const response = await request({
        method: 'POST',
        path: `${ROUTE_CALENDAR}/poll-results`,
        body: { postsIds: eventIds, media_gid },
      })

      const { error, data } = response

      if (!response || error) {
        Alert.error(error || ERROR_MESSAGE, { timeout: 5000 })
      } else {
        setPollResults({ ...data })
      }
    } catch (error) {
      errorHelper({
        error,
        componentName: SliderPollResultsPreview.displayName,
        functionName: 'getResults',
        errorMessage: `Can't get poll results. Please contact support.`,
      })
    } finally {
      setIsGettingResults(false)
    }
  }

  useEffect(() => {
    if (isOpen) {
      getResults()
    }
  }, [isOpen])

  const handleClickCloseSlider = () => {
    if (isGettingResults) {
      Alert.error('Please wait a bit longer while we finish processing data.', { timeout: 5000 })
    } else {
      setSliderPollResultsPreviewData(null)
    }
  }

  const { total_results, total_results_by_profile, title } = pollResults

  return (
    <SwipeableDrawer
      disableBackdropTransition={!iOS}
      disableDiscovery={iOS}
      style={{ zIndex: 2147483001 }}
      anchor="right"
      open={!!isOpen}
      onClose={() => {
        handleClickCloseSlider()
      }}
      disableAutoFocus
      disableEnforceFocus
      onOpen={() => {}}
    >
      {isOpen && (
        <Flex minWidth={isSmallPreview ? '300px' : '650px'} height="100%" mx="auto" bg="background_slider">
          <Scrollbars universal>
            <Flex flexDirection="column" mx={isSmallPreview ? 's' : 'l'} height="calc(80%)">
              {isGettingResults ? (
                <Flex
                  alignItems="center"
                  justifyContent="center"
                  height="100%"
                  width="100%"
                  className="cliploader-wrapper"
                >
                  <ClipLoader size="50" />
                </Flex>
              ) : (
                <Flex flexDirection="column" mt="m" height="100%">
                  <Fragment>
                    <Flex flexDirection="column">
                      <Grid gridGap="m" gridTemplateColumns="1fr" width="100%">
                        {total_results && (
                          <StyledPreviewWrapper $isSmallPreview={isSmallPreview}>
                            <Flex flexDirection="column" px="m">
                              <Text color="primaryText" fontSize="m" py="m">
                                Total poll results based on all publications
                              </Text>

                              <Divider height="1px" />
                            </Flex>

                            <Poll
                              source={{ title, options: total_results }}
                              isLargeView={false}
                              isSmallView={false}
                              isResultsView
                              showDuration={false}
                            />
                          </StyledPreviewWrapper>
                        )}

                        {total_results_by_profile.map(({ profile, results }) => {
                          const {
                            id,
                            name,
                            username,
                            nickname = '',
                            picture_url,
                            network: { code },
                            custom_network_picture_url,
                          } = profile

                          const networkImage = getSocialNetworkIconByName(code, custom_network_picture_url).enabled

                          return (
                            <StyledPreviewWrapper $isSmallPreview={isSmallPreview} key={id}>
                              <Flex flexDirection="column" px="m">
                                <Flex alignItems="center" py="m">
                                  <Flex
                                    width="30px"
                                    height="30px"
                                    justifyContent="center"
                                    alignItems="center"
                                    position="relative"
                                  >
                                    <StyledPicture
                                      width="30px"
                                      height="30px"
                                      source={picture_url || DEFAULT_SOURCE}
                                      fallbackSource={DEFAULT_SOURCE}
                                      objectFit={picture_url ? 'cover' : 'contain'}
                                      id={id}
                                    />
                                    <NetworkImageWrapper>
                                      <Image src={networkImage} width="12px" height="12px" />
                                    </NetworkImageWrapper>
                                  </Flex>

                                  <Text ml="m" color="primaryText">
                                    {nickname || name || username}
                                  </Text>
                                </Flex>

                                <Divider height="1px" />
                              </Flex>

                              <Poll
                                source={{ title, options: results }}
                                isLargeView={false}
                                isSmallView={false}
                                isResultsView
                                showDuration={false}
                              />
                            </StyledPreviewWrapper>
                          )
                        })}
                      </Grid>
                    </Flex>
                  </Fragment>
                </Flex>
              )}
            </Flex>
          </Scrollbars>

          <Flex width="0px" height="0px" onClick={() => handleClickCloseSlider()} className="modal-close-icon" />
        </Flex>
      )}
    </SwipeableDrawer>
  )
}

SliderPollResultsPreview.defaultProps = {
  isSmallPreview: false,
}

SliderPollResultsPreview.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  setSliderPollResultsPreviewData: PropTypes.func.isRequired,
  data: PropTypes.object.isRequired,
  isSmallPreview: PropTypes.bool,
}

SliderPollResultsPreview.displayName = 'SliderPollResultsPreview'

export default SliderPollResultsPreview
